import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const PhotoBooth = ({ setCapturedImages, selectedLayout }) => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [capturedImages, setImages] = useState([]);
  const [filter, setFilter] = useState("none");
  const [cssFilter, setCssFilter] = useState("");
  const [countdown, setCountdown] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [countdownTime, setCountdownTime] = useState(3);
  const [mode, setMode] = useState("capture"); // "capture" or "upload"
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
 
  
  // Get the number of photos required from the selected layout
  const requiredPhotos = selectedLayout ? selectedLayout.poses : 4;

  useEffect(() => {
    if (mode === "capture") {
      startCamera();
    }

    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /android|ipad|iphone|ipod|windows phone/i;
      setIsMobile(mobileRegex.test(userAgent));
    };

    checkMobile();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        startCamera();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, []);

  // Start Camera
  const startCamera = async () => {
    try {
      if (videoRef.current && videoRef.current.srcObject) {
        return;
      }

      const constraints = {
        video: {
          facingMode: { ideal: "user" },
          width: { ideal: isMobile ? 1280 : 1280 },
          height: { ideal: isMobile ? 720 : 720 },
          frameRate: { ideal: 30 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (err) {
          console.error("Error playing video:", err);
        }
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      alert("Could not access your camera. Please ensure camera permissions are granted in your browser settings.");
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  // Switch mode between capture and upload
  const switchMode = (newMode) => {
    setMode(newMode);
    setImages([]);
    setUploadedImages([]);
    setUploadProgress(0);

    if (newMode === "capture") {
      startCamera();
    } else {
      stopCamera();
    }
  };

  // apply filter using canvas api
  const applyFilterToCanvas = (sourceCanvas, filterType) => {
    const ctx = sourceCanvas.getContext("2d");
    
    // Save the original image data before applying filters
    const imageData = ctx.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
    const data = imageData.data;
    
    switch(filterType) {
      case "grayscale(100%)":
        for (let i = 0; i < data.length; i += 4) {
          const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
          data[i] = data[i + 1] = data[i + 2] = avg;
        }
        break;
      case "sepia(100%)":
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          
          data[i] = Math.min(255, (r * 0.393) + (g * 0.769) + (b * 0.189));
          data[i + 1] = Math.min(255, (r * 0.349) + (g * 0.686) + (b * 0.168));
          data[i + 2] = Math.min(255, (r * 0.272) + (g * 0.534) + (b * 0.131));
        }
        break;
      case "grayscale(100%) contrast(120%) brightness(110%) sepia(30%) hue-rotate(10deg) blur(0.4px)":
        // vintage effect
        for (let i = 0; i < data.length; i += 4) {
          const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
          const factor = 1.2; 
          const r = Math.min(255, (avg - 128) * factor + 128 + 25);           
          data[i] = Math.min(255, r * 1.07); 
          data[i + 1] = Math.min(255, r * 0.95); 
          data[i + 2] = Math.min(255, r * 0.8); 
        }
        break;
      case "brightness(130%) contrast(105%) saturate(80%) blur(0.3px)":
        // soft effect
        for (let i = 0; i < data.length; i += 4) {
          const r = Math.min(255, data[i] * 1.3 * 1.05);
          const g = Math.min(255, data[i + 1] * 1.3 * 1.05);
          const b = Math.min(255, data[i + 2] * 1.3 * 1.05);
          const avg = (r + g + b) / 3;
          data[i] = r * 0.8 + avg * 0.2;
          data[i + 1] = g * 0.8 + avg * 0.2;
          data[i + 2] = b * 0.8 + avg * 0.2;
        }
        break;
      case "noir":
        // noir filter
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          
          const gray = Math.min(255, Math.max(0, (r * 0.299 + g * 0.587 + b * 0.114)));
          const contrastGray = Math.min(255, Math.max(0, (gray - 128) * 1.8 + 128));
          
          data[i] = data[i + 1] = data[i + 2] = contrastGray;
          
          const x = (i / 4) % sourceCanvas.width;
          const y = Math.floor((i / 4) / sourceCanvas.width);
          
          const centerX = sourceCanvas.width / 2;
          const centerY = sourceCanvas.height / 2;
          
          const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
          const maxDistance = Math.sqrt(Math.pow(centerX, 2) + Math.pow(centerY, 2));
          
          const vignetteStrength = 0.5; 
          const vignetteFactor = 1 - (vignetteStrength * Math.pow(distance / maxDistance, 2));
          
          data[i] = Math.min(255, Math.max(0, data[i] * vignetteFactor));
          data[i + 1] = Math.min(255, Math.max(0, data[i + 1] * vignetteFactor));
          data[i + 2] = Math.min(255, Math.max(0, data[i + 2] * vignetteFactor));
        }
        break;
      case "vivid":
        // vivid dramatic filter
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          
          const contrastFactor = 1.5;
          const saturationAdjustment = 0.2;   

          const rAdjusted = Math.min(255, Math.max(0, (r - 128) * contrastFactor + 128));
          const gAdjusted = Math.min(255, Math.max(0, (g - 128) * contrastFactor + 128));
          const bAdjusted = Math.min(255, Math.max(0, (b - 128) * contrastFactor + 128));
          
          const max = Math.max(rAdjusted, gAdjusted, bAdjusted);
          const min = Math.min(rAdjusted, gAdjusted, bAdjusted);
          const saturation = max === 0 ? 0 : (max - min) / max;
          const luminance = (max + min) / 2;
                      
          if (max > 0) {
            data[i] = Math.min(255, rAdjusted + (rAdjusted - ((rAdjusted + gAdjusted + bAdjusted) / 3)) * saturationAdjustment);
            data[i + 1] = Math.min(255, gAdjusted + (gAdjusted - ((rAdjusted + gAdjusted + bAdjusted) / 3)) * saturationAdjustment);
            data[i + 2] = Math.min(255, bAdjusted + (bAdjusted - ((rAdjusted + gAdjusted + bAdjusted) / 3)) * saturationAdjustment);
          }
          
          if (luminance > 200) {
            const highlightFactor = 1.05;
            data[i] = Math.min(255, data[i] * highlightFactor);
            data[i + 1] = Math.min(255, data[i + 1] * highlightFactor);
            data[i + 2] = Math.min(255, data[i + 2] * highlightFactor);
          } else if (luminance < 60) {
            const shadowFactor = 1.1;
            data[i] = Math.min(255, data[i] * shadowFactor);
            data[i + 1] = Math.min(255, data[i + 1] * shadowFactor);
            data[i + 2] = Math.min(255, data[i + 2] * shadowFactor);
          }
        }
        break;
      default:
        break;
    }
      
    ctx.putImageData(imageData, 0, 0);
    return sourceCanvas;
  };

  useEffect(() => {
    switch (filter) {
      case "noir":
        setCssFilter("grayscale(100%) contrast(180%) brightness(90%)");
        break;
      case "vivid":
        setCssFilter("contrast(120%) saturate(125%) brightness(102%)");
        break;
      default:
        setCssFilter(filter);
        break;  
    }
  }, [filter]);
 
  // Handle file uploads
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Clear existing uploads if any
    setUploadedImages([]);
    setImages([]);
    
    // Cap at required photos based on layout
    const filesToProcess = files.slice(0, requiredPhotos);
    setUploadProgress(0);
    
    const processedImages = [];
    let processed = 0;
    
    filesToProcess.forEach((file, index) => {
      // Check if file is an image
      if (!file.type.match('image.*')) {
        alert(`File ${file.name} is not an image. Please upload only image files.`);
        return;
      }
      
      const reader = new FileReader();
      
      reader.onload = (e) => {
        // Create an image to get dimensions
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Use 3:4 aspect ratio for photo strip
          canvas.width = 600;
          canvas.height = 450;
          
          // Fill with white background first
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Calculate how to fit and center the image
          let drawWidth, drawHeight, startX, startY;
          const imageRatio = img.width / img.height;
          const canvasRatio = canvas.width / canvas.height;
          
          if (imageRatio > canvasRatio) {
            // Image is wider than canvas ratio
            drawHeight = canvas.height;
            drawWidth = img.width * (canvas.height / img.height);
            startX = (canvas.width - drawWidth) / 2;
            startY = 0;
          } else {
            // Image is taller than canvas ratio
            drawWidth = canvas.width;
            drawHeight = img.height * (canvas.width / img.width);
            startX = 0;
            startY = (canvas.height - drawHeight) / 2;
          }
          
          // Draw the image centered
          ctx.drawImage(
            img,
            startX, startY, drawWidth, drawHeight
          );
          
          // Apply any selected filter
          if (filter !== 'none') {
            applyFilterToCanvas(canvas, filter);
          }
          
          const processedUrl = canvas.toDataURL('image/jpeg', 0.9);
          processedImages[index] = processedUrl;
          
          processed++;
          setUploadProgress(Math.round((processed / filesToProcess.length) * 100));
          
          // When all images are processed, update state
          if (processed === filesToProcess.length) {
            // Filter out any undefined entries
            const filteredImages = processedImages.filter(img => img);
            setUploadedImages(filteredImages);
            setImages(filteredImages);
            
            // If we have fewer than required photos, tell the user
            if (filteredImages.length < requiredPhotos) {
              alert(`You've uploaded ${filteredImages.length} image${filteredImages.length !== 1 ? 's' : ''}. For the best photo strip, please upload exactly ${requiredPhotos} images.`);
            }
          }
        };
        
        img.src = e.target.result;
      };
      
      reader.readAsDataURL(file);
    });
  };

  // Countdown to take photos automatically based on selected layout
  const startCountdown = () => {
    if (capturing) return;
    setCapturing(true);
  
    setImages([]);
    
    let photosTaken = 0;
    const newCapturedImages = [];
    
    const captureSequence = async () => {
      if (photosTaken >= requiredPhotos) {
        setCountdown(null);
        setCapturing(false);

        try {
          setCapturedImages([...newCapturedImages]);
          setTimeout(() => {
            navigate("/picapica-preview");
          }, 300);
        } catch (error) {
          console.error("Error navigating to preview:", error);
          // If navigation fails, at least display the images
          setImages([...newCapturedImages]);
        }
        return;
      }
        
      let timeLeft = countdownTime;
      setCountdown(timeLeft);
  
      const timer = setInterval(() => {
        timeLeft -= 1;
        setCountdown(timeLeft);
  
        if (timeLeft === 0) {
          clearInterval(timer);
          const imageUrl = capturePhoto();
          if (imageUrl) {
            newCapturedImages.push(imageUrl);
            setImages((prevImages) => [...prevImages, imageUrl]);
          }
          photosTaken += 1;
          setTimeout(captureSequence, 1000);
        }
      }, 1000);
    };
  
    captureSequence();
  };

  // Capture Photo
  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (video && canvas) {
        const context = canvas.getContext("2d");

        console.log("Device Info:", {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          touchPoints: navigator.maxTouchPoints,
          isMobile
        });

        const targetWidth = 1280;
        const targetHeight = 720;

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const videoRatio = video.videoWidth / video.videoHeight;
        const targetRatio = targetWidth / targetHeight;
        
        let drawWidth = video.videoWidth;
        let drawHeight = video.videoHeight;
        let startX = 0;
        let startY = 0;

        if (videoRatio > targetRatio) {
            drawWidth = drawHeight * targetRatio;
            startX = (video.videoWidth - drawWidth) / 2;
        } else {
            drawHeight = drawWidth / targetRatio;
            startY = (video.videoHeight - drawHeight) / 2;
        }

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = targetWidth;
        tempCanvas.height = targetHeight;
        const tempCtx = tempCanvas.getContext('2d');
        
        // Draw the original image first
        tempCtx.save();
        tempCtx.translate(tempCanvas.width, 0);
        tempCtx.scale(-1, 1);
        tempCtx.drawImage(
          video,
          startX, startY, drawWidth, drawHeight,
          0, 0, targetWidth, targetHeight
        );
        tempCtx.restore();

        // Flip canvas for mirroring
        context.save();

        if (filter !== 'none') {
          applyFilterToCanvas(tempCanvas, filter);
        }
        
        // Draw the processed image to the main canvas
        context.drawImage(tempCanvas, 0, 0);

        return canvas.toDataURL("image/png");
    }
};

// Proceed to preview with uploaded images
const proceedToPreview = () => {
  if (uploadedImages.length === 0) {
    alert("Please upload at least one image first.");
    return;
  }

  setCapturedImages([...uploadedImages]);
  navigate("/picapica-preview");
};

return (
  <div className="photo-booth">
    {/* Mode Selection Buttons */}
    <div className="mode-selection" style={{ marginTop: "40px", marginBottom: "20px" }}>
      <button 
        onClick={() => switchMode("capture")}
        className={mode === "capture" ? "active-mode" : ""}
        style={{ 
          backgroundColor: mode === "capture" ? "#000000" : "transparent",
          color: mode === "capture" ? "white" : "black",
          padding: "10px 16px",
          border: "2px solid #333",
          borderRadius: "25px",
          fontSize: "0.9rem",
          fontWeight: "bold",
          transition: "all 0.3s ease",
          width: "150px",
          maxWidth: "150px"
        }}
      >
        Camera
      </button>
      <button 
        onClick={() => switchMode("upload")}
        className={mode === "upload" ? "active-mode" : ""}
        style={{ 
          backgroundColor: mode === "upload" ? "#000000" : "transparent",
          color: mode === "upload" ? "white" : "black",
          padding: "10px 16px",
          border: "2px solid #333",
          borderRadius: "25px",
          fontSize: "0.9rem",
          fontWeight: "bold",
          transition: "all 0.3s ease",
          width: "150px",
          maxWidth: "150px"
        }}
      >
        Upload Images
      </button>
    </div>

    {/* Show selected layout information */}
    <div className="layout-info-banner" style={{ 
      margin: "0 auto 20px auto", 
      padding: "8px 15px", 
      backgroundColor: "rgba(255, 153, 178, 0.2)", 
      borderRadius: "10px",
      maxWidth: "500px"
    }}>
      <p style={{ margin: 0, fontSize: "0.9rem" }}>
        Selected layout: {selectedLayout ? selectedLayout.type.replace('layout', 'Layout ') : 'Layout B'} 
        ({requiredPhotos} photos)
      </p>
    </div>

    {/* Content Container with fixed minimum height to maintain layout consistency */}
    <div style={{ minHeight: "450px" }}>
      {mode === "capture" && (
        <div className="photo-container">
          <div className="camera-container">
          {countdown !== null && <h2 className="countdown animate">{countdown}</h2>}
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted 
              disablePictureInPicture 
              disableRemotePlayback
              className="video-feed" 
              style={{ 
                filter: cssFilter,
                width: '100%',
                height: '100%',
                objectFit: 'cover'  
              }}
            />        
            <canvas ref={canvasRef} className="hidden" />
          </div>

          <div className="preview-side">
            {capturedImages.map((image, index) => (
              <img 
                key={index} 
                src={image} 
                alt={`Captured ${index + 1}`} 
                className="side-preview"
                style={{ 
                  width: "160px", 
                  height: "120px", 
                  objectFit: "cover",
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                  marginBottom: "10px"
                }}
              />
            ))}        
          </div>
        </div>
      )}

      {mode === "upload" && (
        <div className="upload-container" style={{ marginBottom: "20px" }}>
          <div className="upload-instructions" style={{ marginBottom: "15px" }}>
            <p>Choose {requiredPhotos} images from your photos to create a photo strip.</p>
          </div>
          
          <input 
            type="file" 
            accept="image/*" 
            multiple 
            onChange={handleFileUpload}
            ref={fileInputRef}
            style={{ display: "none" }}
          />
          
          <button 
            onClick={() => fileInputRef.current.click()}
            style={{ marginBottom: "15px" }}
          >
            Select Images
          </button>
          
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="upload-progress" style={{ marginBottom: "15px" }}>
              <div style={{ width: "100%", backgroundColor: "#f0f0f0", borderRadius: "10px", height: "10px" }}>
                <div 
                  style={{ 
                    width: `${uploadProgress}%`, 
                    backgroundColor: "#ff9be4", 
                    height: "10px", 
                    borderRadius: "10px",
                    transition: "width 0.3s ease-in-out"
                  }} 
                />
              </div>
              <p style={{ fontSize: "0.8rem", marginTop: "5px" }}>Processing images: {uploadProgress}%</p>
            </div>
          )}
          
          {uploadedImages.length > 0 && (
            <div className="upload-preview-container" style={{ marginTop: "20px" }}>
              <h3>Uploaded Images</h3>
              <div className="preview-side" style={{ 
                display: "flex", 
                flexDirection: "column",
                justifyContent: "center", 
                gap: "10px",
                marginTop: "10px",
                width: "160px" 
              }}>
                {uploadedImages.map((image, index) => (
                  <img 
                    key={index} 
                    src={image} 
                    alt={`Uploaded ${index + 1}`} 
                    className="side-preview"
                    style={{ 
                      width: "160px", 
                      height: "120px", 
                      objectFit: "cover",
                      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                      marginBottom: "10px"
                    }}
                  />
                ))}
              </div>
              
              <button 
                onClick={proceedToPreview}
                style={{ marginTop: "20px" }}
                disabled={uploadedImages.length === 0}
              >
                Continue to Photo Strip Editor
              </button>
            </div>
          )}
        </div>
      )}
    </div>

    {mode === "capture" && (
      <>
        {/* Countdown Selection */}
        <div className="countdown-options">
          <label>Select Countdown Time:</label>
          <select onChange={(e) => setCountdownTime(parseInt(e.target.value))} value={countdownTime} disabled={capturing}>
            <option value={3}>3s</option>
            <option value={5}>5s</option>
            <option value={10}>10s</option>
          </select>
        </div>
        
        <div className="controls">
          <button onClick={startCountdown} disabled={capturing}>
            {capturing ? "Capturing..." : `Start Capture (${requiredPhotos} photos)`}
          </button>
        </div>
      </>
    )}
   
    <p className="filter-prompt">Choose a filter for your photos!</p>

    <div className="filters">
      <button onClick={() => setFilter("none")} disabled={mode === "capture" && capturing}>No Filter</button>
      <button onClick={() => setFilter("grayscale(100%)")} disabled={mode === "capture" && capturing}>B&W</button>
      <button onClick={() => setFilter("sepia(100%)")} disabled={mode === "capture" && capturing}>Sepia</button>
      <button onClick={() => setFilter("grayscale(100%) contrast(120%) brightness(110%) sepia(30%) hue-rotate(10deg) blur(0.4px)")} disabled={mode === "capture" && capturing}>Vintage</button>
      <button onClick={() => setFilter("brightness(130%) contrast(105%) saturate(80%) blur(0.3px)")} disabled={mode === "capture" && capturing}>Soft</button>
      <button onClick={() => setFilter("noir")} disabled={mode === "capture" && capturing}>Noir</button>
      <button onClick={() => setFilter("vivid")} disabled={mode === "capture" && capturing}>Vivid</button>
      
    </div>

  </div>
    
);
};

export default PhotoBooth;